import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { EP_BASE_API } from '../constants/endPointConstants';
import { getValidAccessToken } from './SessionRefresh';
import { getRoomMembershipVersion, getRoomCredential } from './RoomSession';

const CONNECTION_STATUS = Object.freeze({
  idle: 'idle',
  initializing: 'initializing',
  synchronizing: 'synchronizing',
  connected: 'connected',
  reconnecting: 'reconnecting',
  disconnected: 'disconnected',
});

export class SignalRService {
  constructor(connection) {
    this.connection = connection || new HubConnectionBuilder()
      .withUrl(`${EP_BASE_API || ''}/voiceHub`, {
        accessTokenFactory: () => this._getConnectionToken(),
        withCredentials: true,
        // Prefer one persistent socket. Polling is only a proxy/browser fallback.
        // The API accepts browser query credentials only on the WebSocket hub.
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      // One coordinator owns initial start AND reconnect. SignalR's default
      // automatic retry policy otherwise keeps running in hidden browsers.
      .configureLogging(signalR.LogLevel.None)
      .build();

    // Allow brief mobile/tunnel latency without mistaking it for a dead server.
    this.connection.serverTimeoutInMilliseconds = 60000;
    this.connection.keepAliveIntervalInMilliseconds = 15000;

    this.onConnected = null;
    this.onJoin = null;
    this.onLeave = null;
    this.onStateChange = null;
    this.onSendMessage = null;
    this.onPlayerJoin = null;
    this.onPlayerLeave = null;
    this.onConnectionLost = null;
    this.connection.on('MembershipEnded', (message) => {
      if (message?.membershipVersion != null && String(message.membershipVersion) !== getRoomMembershipVersion(message.roomId)) return;
      const graceRecovery = Date.parse(message?.autoRejoinUntilUtc || '') > Date.now();
      if (!graceRecovery && this.roomMembership?.roomId === message?.roomId) {
        this.roomMembership = null;
        this.membershipEpoch += 1;
        this.joinedGeneration = -1;
      }
      window.dispatchEvent(new CustomEvent('pwf-room-membership-ended', { detail: message }));
    });
    this.onReconnected = null;
    this.connection.on('RoomEntryChanged', message => {
      window.dispatchEvent(new CustomEvent('pwf-room-entry-changed', { detail: message }));
    });
    this.connection.on('SubscriptionChanged', () => {
      window.dispatchEvent(new CustomEvent('pwf-subscription-changed'));
    });

    this.connectionStatus = CONNECTION_STATUS.idle;
    this.connectionRequired = false;
    this.startPromise = null;
    this.disconnectTimer = null;
    this.roomMembership = null;
    this.joinPromise = null;
    this.connectionGeneration = 0;
    this.transportMembershipKey = null;
    this.roomWasReady = false;
    this.attemptPhase = null;
    this.lastFailure = null;
    this.joinedGeneration = -1;
    this.membershipEpoch = 0;
    this.recovery = null;
    this.recoveryTimer = null;
    this.restoreRoom = null;
    this.attemptAbort = null;
    this.suspended = false;
    this.lifecycleAttached = false;
    this.visibilityChanged = () => this._visibilityChanged();
    this.pageHidden = () => { this.suspended = true; this._visibilityChanged(); };
    this.pageShown = () => { this.suspended = false; this._visibilityChanged(true); };
    this.online = () => { if (this.isVisible() && this.connectionRequired) this.retry(); };

    this.connection.on('ReceiveConnected', (connectionId) => {
      if (this.onConnected) this.onConnected(connectionId);
    });
    this.connection.on('ReceiveJoinRoom', (roomData) => {
      if (this.onJoin) this.onJoin(roomData);
    });
    this.connection.on('ReceiveLeaveRoom', (roomData) => {
      if (this.onLeave) this.onLeave(roomData);
    });
    this.connection.on('ReceiveStateChange', (roomData) => {
      if (this.onStateChange) this.onStateChange(roomData);
    });
    this.connection.on('ProfilePictureChanged', detail => {
      window.dispatchEvent(new CustomEvent('pwf-profile-picture-changed', { detail }));
    });
    this.connection.on('ReceiveSendMessage', (chatInfos) => {
      if (this.onSendMessage) this.onSendMessage(chatInfos);
    });
    this.connection.on('NotifyPlayerJoin', (player) => {
      if (this.onPlayerJoin) this.onPlayerJoin(player);
    });
    this.connection.on('NotifyPlayerLeave', (player) => {
      if (this.onPlayerLeave) this.onPlayerLeave(player);
    });

    // These are registration methods in @microsoft/signalr. Assigning to
    // `onclose`/`onreconnected` replaces the methods and never registers the
    // callbacks, so always call them instead.
    this.connection.onreconnecting(() => {
      this.connectionGeneration += 1;
      this.connectionStatus = CONNECTION_STATUS.reconnecting;
      this._scheduleConnectionLost();
    });
    this.connection.onreconnected(async () => {
      this.connectionStatus = CONNECTION_STATUS.reconnecting;
      this.connectionGeneration += 1;
      try {
        await this._restoreRoomMembership();
        if (!this.isReady()) this._markConnected();
      } catch (error) {
        this._recoveryFailed(error, 'subscription');
      }
    });
    this.connection.onclose(() => {
      this.connectionStatus = CONNECTION_STATUS.disconnected;
      this._scheduleConnectionLost();
    });
  }

  setRequired(required) {
    this.connectionRequired = Boolean(required);
    if (!this.connectionRequired) {
      this.attemptAbort?.abort();
      this.membershipEpoch += 1;
      this.roomMembership = null;
      this.joinedGeneration = -1;
      this.restoreRoom = null;
      this._clearRecovery();
      this._clearDisconnectTimer();
      if (this.onReconnected) this.onReconnected();
    }
    this._publishStatus();
  }

  _getConnectionToken() { return getRoomCredential(this.roomMembership?.roomId) || getValidAccessToken(); }

  configureRoomRecovery(roomId, playerId, restore) {
    this.membershipEpoch += 1;
    this.roomMembership = { roomId, playerId, key: `${roomId}:${playerId}:${getRoomMembershipVersion(roomId)}` };
    this.joinedGeneration = -1;
    this.restoreRoom = restore;
    this.roomWasReady = false;
    this.lastFailure = null;
    this.connectionStatus = CONNECTION_STATUS.initializing;
    this._publishStatus();
  }

  isVisible() { return !this.suspended && document.visibilityState !== 'hidden'; }

  _attachLifecycle() {
    if (this.lifecycleAttached) return;
    this.lifecycleAttached = true;
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('pagehide', this.pageHidden);
    window.addEventListener('pageshow', this.pageShown);
    window.addEventListener('online', this.online);
  }

  dispose() {
    this.attemptAbort?.abort();
    this.connectionRequired = false;
    this.membershipEpoch += 1;
    this.restoreRoom = null;
    this._clearRecovery();
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('pagehide', this.pageHidden);
    window.removeEventListener('pageshow', this.pageShown);
    window.removeEventListener('online', this.online);
    this.lifecycleAttached = false;
  }

  _publishStatus() {
    window.dispatchEvent(new CustomEvent('pwf-connection-state', { detail: {
      ready: this.isReady(), status: this.connectionStatus, required: this.connectionRequired,
      transportConnected: this.isConnected(), exhausted: Boolean(this.recovery?.exhausted), lastFailure: this.lastFailure,
    } }));
  }

  _clearRecovery() {
    clearTimeout(this.recoveryTimer);
    this.recoveryTimer = null;
    this.recovery = null;
  }

  _visibilityChanged(resume = false) {
    if (!this.isVisible()) {
      this.attemptAbort?.abort();
      this._countVisibleTime();
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
      if (this.recovery) this.recovery.lastTick = null;
      // Cancel an in-flight handshake; keep an already connected socket.
      if (this.connection.state === HubConnectionState.Connecting) this.connection.stop?.().catch(() => {});
      window.dispatchEvent(new CustomEvent('pwf-connection-hidden'));
      return;
    }
    if (!this.connectionRequired) return;
    if (this.recovery) this.recovery.lastTick = Date.now();
    // Even a surviving socket needs a fresh authority/state check on return.
    if (this.isConnected() && (resume || !this.recovery)) this.joinedGeneration = -1;
    this._scheduleConnectionLost();
  }

  _countVisibleTime() {
    const cycle = this.recovery;
    if (cycle?.lastTick != null) {
      // A suspended browser can delay a timer by minutes without delivering
      // visibility events. Never charge that entire suspension on resume.
      cycle.remaining -= Math.min(1000, Math.max(0, Date.now() - cycle.lastTick));
      cycle.lastTick = Date.now();
    }
  }

  dismissRecoveryWarning() { if (this.recovery) this.recovery.dismissed = true; }

  requestRecovery() {
    if (!this.connectionRequired || this.recovery) return;
    this.joinedGeneration = -1;
    this.connectionStatus = this.isConnected() ? CONNECTION_STATUS.synchronizing : CONNECTION_STATUS.disconnected;
    this._scheduleConnectionLost();
  }

  retry() {
    if (!this.isVisible()) return;
    this._clearRecovery();
    if (this.isConnected()) this.joinedGeneration = -1;
    this._scheduleConnectionLost();
  }

  isRequired() {
    return this.connectionRequired;
  }

  isConnected() {
    return this.connection?.state === HubConnectionState.Connected;
  }

  getStatus() {
    return this.connectionStatus;
  }

  isReady() {
    return this.isConnected() && this.connectionStatus === CONNECTION_STATUS.connected &&
      (!this.transportMembershipKey || this.transportMembershipKey === this.roomMembership?.key) &&
      (!this.connectionRequired || (this.roomMembership !== null && this.joinedGeneration === this.connectionGeneration));
  }

  _clearDisconnectTimer() {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }

  _scheduleConnectionLost() {
    this._clearDisconnectTimer();
    if (!this.connectionRequired) return;
    this._attachLifecycle();
    if (!this.recovery) this.recovery = { remaining: 10000, lastTick: this.isVisible() ? Date.now() : null, nextAttempt: 0, exhausted: false, dismissed: false };
    this._publishStatus();
    if (!this.isVisible() || this.recovery.exhausted || this.recoveryTimer) return;
    this.recoveryTimer = setTimeout(() => this._recoveryTick(), 250);
  }

  _recoveryTick() {
    this.recoveryTimer = null;
    const cycle = this.recovery;
    if (!cycle || !this.isVisible() || !this.connectionRequired) return;
    if (this.isReady()) { this._markConnected(); return; }
    this._countVisibleTime();
    if (cycle.remaining <= 0) {
      cycle.exhausted = true;
      this.attemptAbort?.abort();
      // A failed sync is not a lost transport. Keep a healthy connection open.
      this.connectionStatus = this.isConnected() ? CONNECTION_STATUS.synchronizing : CONNECTION_STATUS.disconnected;
      if (!this.isConnected() && !cycle.dismissed && this.onConnectionLost) this.onConnectionLost();
      if (this.connection.state === HubConnectionState.Connecting)
        this.connection.stop?.().catch(() => {});
      this._publishStatus();
      return;
    }
    if (!this.startPromise && Date.now() >= cycle.nextAttempt) {
      cycle.nextAttempt = Date.now() + 1000;
      this.start().catch(() => {});
    }
    this._scheduleConnectionLost();
  }

  _markConnected() {
    this.connectionStatus = CONNECTION_STATUS.connected;
    this._clearDisconnectTimer();
    if (this.isReady()) {
      this.roomWasReady = true;
      this.lastFailure = null;
      this._clearRecovery();
      if (this.onReconnected) this.onReconnected();
    }
    this._publishStatus();
  }

  async _startWithRetry() {
    const abort = new AbortController();
    this.attemptAbort = abort;
    const epoch = this.membershipEpoch;
    this.connectionStatus = this.isConnected() ? CONNECTION_STATUS.synchronizing
      : this.roomWasReady ? CONNECTION_STATUS.reconnecting : CONNECTION_STATUS.initializing;
    this.attemptPhase = 'membership';
    this._publishStatus();
    // Validate the seat before authenticating the transport. An ended proof
    // otherwise fails the hub handshake forever without reaching REST's clear
    // membership-ended response (for example after timeout or server restart).
    if (this.roomMembership && this.restoreRoom) {
      if (!(await this.restoreRoom(abort.signal)) || epoch !== this.membershipEpoch || !this.isVisible()) return null;
      const { roomId, playerId } = this.roomMembership;
      this.roomMembership = { roomId, playerId, key: `${roomId}:${playerId}:${getRoomMembershipVersion(roomId)}` };
    }
    const scopeKey = getRoomCredential(this.roomMembership?.roomId) ? this.roomMembership.key : null;
    // A capability-authenticated hub connection cannot be reused for another
    // room/version or for account notifications in the lobby.
    if (this.isConnected() && this.transportMembershipKey !== scopeKey)
      await this.connection.stop();
    if (epoch !== this.membershipEpoch || !this.isVisible()) return null;
    if (!this.isConnected()) {
      if (this.connection.state !== HubConnectionState.Disconnected) return null;
      this.attemptPhase = 'transport';
      await this.connection.start();
      this.transportMembershipKey = scopeKey;
      this.connectionGeneration += 1;
    }
    if (!this.isVisible() || epoch !== this.membershipEpoch) return null;
    this.attemptPhase = 'subscription';
    await this._restoreRoomMembership(abort.signal, true);
    if (epoch !== this.membershipEpoch || !this.isVisible()) return null;
    if (!this.isReady()) this._markConnected();
    return true;
  }

  async start() {
    this._attachLifecycle();
    if (!this.isVisible()) return null;
    if (this.isReady()) return true;
    if (this.startPromise) { this._scheduleConnectionLost(); return this.startPromise; }
    this._scheduleConnectionLost();
    const epoch = this.membershipEpoch;
    this.startPromise = this._startWithRetry()
      .catch((error) => {
        if (epoch !== this.membershipEpoch) return null;
        this._recoveryFailed(error, this.attemptPhase);
        throw error;
      })
      .finally(() => {
        this.startPromise = null;
        this.attemptAbort = null;
      });

    return this.startPromise;
  }

  _recoveryFailed(error, phase) {
    this.connectionStatus = this.isConnected() ? CONNECTION_STATUS.synchronizing : CONNECTION_STATUS.disconnected;
    if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
      const status = error?.response?.status || error?.statusCode;
      const failure = { phase: phase || 'unknown', statusCode: Number.isInteger(status) ? status : null };
      if (JSON.stringify(failure) !== JSON.stringify(this.lastFailure))
        console.warn('[GaSarMal room recovery] Request failed', failure); // No URLs, tokens or raw Axios errors.
      this.lastFailure = failure;
    }
    this._scheduleConnectionLost();
  }

  _withAbort(promise, signal) {
    if (!signal) return promise;
    return new Promise((resolve, reject) => {
      const abort = () => { const error = new Error('Room recovery paused.'); error.name = 'AbortError'; reject(error); };
      if (signal.aborted) abort();
      else signal.addEventListener('abort', abort, { once: true });
      Promise.resolve(promise).then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    });
  }

  async _invoke(method, ...args) {
    if (!this.isConnected()) {
      if (this.connectionStatus !== CONNECTION_STATUS.initializing) {
        this.connectionStatus = CONNECTION_STATUS.disconnected;
        this._scheduleConnectionLost();
      }
      const error = new Error('The room connection is not available.');
      error.code = 'ROOM_CONNECTION_UNAVAILABLE';
      throw error;
    }

    try {
      return await this.connection.invoke(method, ...args);
    } catch (error) {
      if (!this.isConnected()) this._recoveryFailed(error, 'transport');
      throw error;
    }
  }

  async joinRoom(roomId, playerId, signal) {
    const version = Number(getRoomMembershipVersion(roomId));
    const generation = this.connectionGeneration;
    const membershipKey = `${roomId}:${playerId}:${version}`;
    if (this.roomMembership?.key !== membershipKey) {
      this.membershipEpoch += 1;
      this.roomMembership = { key: membershipKey, roomId, playerId };
      this.joinedGeneration = -1;
    }
    const epoch = this.membershipEpoch;
    if (
      this.isConnected() &&
      this.roomMembership?.key === membershipKey &&
      this.joinedGeneration === generation
    ) {
      return true;
    }
    if (this.joinPromise?.key === membershipKey && this.joinPromise.generation === generation && this.joinPromise.epoch === epoch) return this.joinPromise.promise;

    const promise = this._withAbort(this._invoke('JoinRoom', roomId, playerId, version), signal)
      .then((result) => {
        // A delayed response from the old socket cannot subscribe the new one.
        // A fulfilled invocation is success, including result:null from a Task
        // hub method. Failed invocations reject; null is not a failure sentinel.
        if (this.isConnected() && this.connectionGeneration === generation && this.membershipEpoch === epoch) {
          this.roomMembership = { key: membershipKey, roomId, playerId };
          this.joinedGeneration = generation;
          this._markConnected();
        }
        return result;
      })
      .catch(error => {
        if (!signal?.aborted && this.membershipEpoch === epoch && this.connectionGeneration === generation)
          this._recoveryFailed(error, 'subscription');
        throw error;
      })
      .finally(() => {
        if (this.joinPromise?.promise === promise) this.joinPromise = null;
      });

    this.joinPromise = { key: membershipKey, generation, epoch, promise };
    return promise;
  }

  async _restoreRoomMembership(signal, alreadyRestored = false) {
    if (!this.roomMembership || !this.isConnected()) return;
    const epoch = this.membershipEpoch;
    const { roomId, playerId } = this.roomMembership;
    if (!alreadyRestored && this.restoreRoom && !(await this.restoreRoom(signal))) return;
    if (epoch !== this.membershipEpoch || !this.isVisible()) return;
    await this.joinRoom(roomId, playerId, signal);
  }

  async leaveRoom(roomId, playerId) {
    if (this.roomMembership?.roomId === roomId && this.roomMembership?.playerId === playerId) {
      this.roomMembership = null;
      this.membershipEpoch += 1;
      this.joinedGeneration = -1;
    }
    const epoch = this.membershipEpoch;
    try {
      return await this._invoke('LeaveRoom', roomId, playerId);
    } finally {
      if (this.membershipEpoch === epoch && this.roomMembership?.roomId === roomId && this.roomMembership?.playerId === playerId) {
        this.roomMembership = null;
        this.joinedGeneration = -1;
      }
    }
  }

  async stateChange(roomId, playerId) {
    return this._invoke('StateChange', roomId, playerId);
  }

  async sendMessage(roomId, playerId) {
    return this._invoke('SendMessage', roomId, playerId);
  }
}

export { CONNECTION_STATUS };
const signalRService = new SignalRService();
export default signalRService;
